import React from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-location';
import { toast } from 'react-toastify';

import { Column, Row } from 'Core/components/Layout';
import { PrimaryButton, OutlineButton } from 'Core/components/Button';
import Loader from 'Core/components/Loader';
import cacheKeys from 'Core/hooks/queries/cacheKeys';
import useAlertStore, { clearAlertSelector, setErrorAlertSelector } from 'Core/stores/alert';
import { getUrlParamIgnoreCase } from 'Core/utils/utils';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';

import useUpdateOffer from 'Modules/fortiva/hooks/useUpdateOffer';
import useGetApplication from 'Modules/fortiva/hooks/useGetApplication';
import useAccountLookup from 'Modules/fortiva/hooks/useAccountLookup';
import OtpInput from 'Modules/fortiva/components/OtpInput';
import OfferTermsAndConditions from 'Modules/fortiva/components/OfferTermsAndConditions';
import constants from 'Modules/fortiva/config/constants';
import { createFinancingPath } from 'Modules/fortiva/routes';
import { hasOtpMismatch, isOtpMismatchStatus } from 'Modules/fortiva/utils/correctionStatus';

import FinancingSummary from './FinancingSummary';

const OFFER_ID_LENGTH = 6;
const UPDATE_OFFER_POLL_INTERVAL = 3000;

export default function FinalizerOffer({
  application,
  customerIdentityNumber,
  watch,
  pendingFinalize,
  selectedOffer,
  setRefetchInterval,
  routes,
  onBack,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setErrorAlert = useAlertStore(setErrorAlertSelector);
  const clearAlert = useAlertStore(clearAlertSelector);
  const { data: paymentInfo } = useGetPaymentInfo() || {};
  const maskedEmail = paymentInfo?.masked_email || paymentInfo?.email || '';

  const nextAction = application?.next_action;
  const isUpdateOffer = nextAction === 'UPDATE_OFFER';

  const { mutateAsync: updateOffer, isLoading: isUpdating } = useUpdateOffer();
  const { mutate: accountLookup, isLoading: isResendingOtp } = useAccountLookup();

  const handleResendOtp = () => {
    accountLookup(customerIdentityNumber, {
      onSuccess: () =>
        toast.success(t('fortiva.finalize.otp.resendToast', { defaultValue: 'OTP sent!' })),
    });
  };

  const [offerCode, setOfferCode] = React.useState('');
  const [terms, setTerms] = React.useState([false, false, false, false]);
  const [otpError, setOtpError] = React.useState(() =>
    hasOtpMismatch(application) ? t('fortiva.finalize.otp.invalidCode') : ''
  );

  const handleOfferCodeChange = (next) => {
    if (otpError) setOtpError('');
    setOfferCode(next);
  };

  const [updateOfferRequestId, setUpdateOfferRequestId] = React.useState('');
  const [isPollingUpdateOffer, setIsPollingUpdateOffer] = React.useState(false);

  const getCorrectionStatus = (app) => app?.payload?.correction_required?.status ?? null;

  const { data: polledApp, isSuccess: isPolledAppSuccess } = useGetApplication(
    updateOfferRequestId,
    isPollingUpdateOffer ? UPDATE_OFFER_POLL_INTERVAL : false
  );

  React.useEffect(() => {
    if (!isPollingUpdateOffer || !isPolledAppSuccess || !polledApp) {
      return;
    }

    setIsPollingUpdateOffer(false);
    setUpdateOfferRequestId('');

    if (isOtpMismatchStatus(getCorrectionStatus(polledApp))) {
      setOtpError(t('fortiva.finalize.otp.invalidCode'));
      setOfferCode('');
      return;
    }

    sessionStorage.setItem(constants.SESS_STORAGE_APPLICATION_KEY, JSON.stringify(polledApp));
    queryClient.invalidateQueries(cacheKeys.financingNextStep);
    navigate({ to: createFinancingPath, search: true });
  }, [isPollingUpdateOffer, isPolledAppSuccess, polledApp, queryClient, navigate, t]);

  React.useEffect(() => {
    return () => {
      setUpdateOfferRequestId('');
      queryClient.removeQueries(cacheKeys.fortivaGetApplication);
    };
  }, [queryClient]);

  const blockFinalizeOfferForFinalQuote =
    getUrlParamIgnoreCase('blockFinalizeOfferForFinalQuote') === 'true';

  const allTermsAccepted = terms.every(Boolean);
  const offerCodeValid = offerCode.length === OFFER_ID_LENGTH;
  const canFinalize =
    !blockFinalizeOfferForFinalQuote && allTermsAccepted && (isUpdateOffer || offerCodeValid);

  const getOfferDetails = () => {
    if (pendingFinalize) return pendingFinalize;
    if (typeof watch !== 'function') return null;
    const downPayment = Number(watch('downPayment')) || 0;
    const deliveryDate = watch('deliveryDate');
    if (!deliveryDate) return null;
    const alreadyPaid = application?.payload?.already_paid_amount ?? 0;
    return {
      downPayment,
      deliveryDate,
      amountToCollect: Math.round((downPayment - alreadyPaid) * 100) / 100,
    };
  };

  const prepareCollectPayment = async () => {
    const offerDetails = getOfferDetails();
    if (!offerDetails) {
      setErrorAlert(t('fortiva.finalize.missingOfferDetails'));
      throw new Error('missing offer details');
    }

    try {
      setOtpError('');
      await updateOffer({
        offerId: '',
        processSale: false,
        additionalPayment: offerDetails.downPayment,
        financingDownPayment: offerDetails.amountToCollect,
        deliveryDate: offerDetails.deliveryDate,
      });
      queryClient.invalidateQueries(cacheKeys.financingPlatformApplication);
      clearAlert();
    } catch (err) {
      setErrorAlert(err.message);
      throw err;
    }
  };

  const handleFinalize = async () => {
    const offerDetails = getOfferDetails();
    if (!offerDetails) {
      setErrorAlert(t('fortiva.finalize.missingOfferDetails'));
      return;
    }

    try {
      setOtpError('');
      const result = await updateOffer({
        offerId: isUpdateOffer ? '' : offerCode,
        processSale: nextAction === 'SELECT_OFFER' || nextAction === 'UPDATE_OFFER',
        additionalPayment: offerDetails.downPayment,
        // Fortiva expects only the amount collected in the down-payment step,
        // i.e. the entered down payment minus the already-paid amount.
        financingDownPayment: offerDetails.amountToCollect,
        deliveryDate: offerDetails.deliveryDate,
      });

      const requestId = result?.headers?.request_id;
      if (requestId) {
        setUpdateOfferRequestId(requestId);
        setIsPollingUpdateOffer(true);
      }
      queryClient.invalidateQueries(cacheKeys.financingPlatformApplication);
      clearAlert();
    } catch (err) {
      setErrorAlert(err.message);
    }
  };

  return (
    <>
      {(isUpdating || isPollingUpdateOffer) && (
        <Loader message={t('fortiva.updateApplication.loaderText')} />
      )}
      <div className="w-full m-auto">
        <FinancingSummary
          watch={watch}
          setRefetchInterval={setRefetchInterval}
          routes={routes}
          application={application}
          selectedOffer={selectedOffer}
          onPrepareCollectPayment={prepareCollectPayment}
        />

        <div className="mt-4 space-y-4">
          <OfferTermsAndConditions value={terms} onChange={setTerms} />
          {!isUpdateOffer && (
            <OtpInput
              value={offerCode}
              onChange={handleOfferCodeChange}
              maskedEmail={maskedEmail}
              error={otpError}
              onResend={handleResendOtp}
              isResending={isResendingOtp}
            />
          )}
          <div
            className="rounded-xl border border-slate-300 bg-gradient-to-br from-slate-50 to-blue-50/40 px-4 py-3 text-sm text-slate-700 leading-relaxed"
            data-testid="finalize-authorization-note"
          >
            <span className="font-bold text-slate-900">Note:</span> Fortiva® Retail Credit is itself
            a credit account, so a separate $98 authorization hold is not required. Click below to
            finalize the account.
          </div>
        </div>

        <Row className="flex items-center justify-between mt-4 px-4">
          <Column className="md:!flex-col-3">
            <OutlineButton block type="button" data-testid="back-to-down-payment" onClick={onBack}>
              {t('fortiva.back')}
            </OutlineButton>
          </Column>
          <Column className="md:!flex-col-6">
            <PrimaryButton
              block
              data-testid="finalize-fortiva-offer"
              onClick={handleFinalize}
              disabled={!canFinalize || isUpdating}
            >
              {t('fortiva.finalize.cta')}
            </PrimaryButton>
          </Column>
        </Row>
      </div>
    </>
  );
}
